/************************************************************************************************************
Author                          : Trizent Technologies Private Limited
Date                            : 
Notes/Remarks                   : 

Organization:  	Trizent Technologies Private Limited.
		Module No. 17, SBC, Thejaswini Building
		Phase 1, Technopark, Thiruvananthapuram - 695581, 
		Kerala, India
 
Copyright 2026 (C) Trizent Technologies Pvt Ltd. (www.trizentinc.com)
All rights reserved.
***************************************************************************************************************/

import { useTranslation } from "react-i18next";
import MediaPrepHeaderSection from "../../../../common/commonComponents/mediaPrepheaderSection";
import MediaPrepPageHeader from "../../../../common/commonComponents/mediaPrepPageHeader";
import ReconTitleHeader from "../../common/ReconTitleHeader";
import { DEFAULT_PAGE_SIZE } from "../../../../../constants/constants";
import ReconSearchFilter from "../../common/ReconSearchFilter";
import ReconStatCard from "../../common/ReconStatsCard";
import {
	ReconMediaBatchDetails,
	ReconMediaBatchListResponse,
} from "../reconcileMediaBatch.types";
import { mediaReconciliationService } from "../../../../../service/mediaReconciliationServices";
import { useEffect, useState } from "react";
import { getMediaBatchReconciliationColumns } from "../partials/columns";
import MediaPreparationTable from "../../../../common/commonComponents/mediaPreparationTable";
import Pagination from "../../../../../widgets/pagination";
import { userService } from "../../../../../service/userService";

type Props = {
	setCurrentView: (row: ReconMediaBatchDetails) => void;
};

const ReconMediaBatchListView: React.FC<Props> = ({ setCurrentView }) => {
	const [searchTerm, setSearchTerm] = useState<string>("");
	const [totalTableElements, setTotalTableElements] = useState<number>(0);
	const { t } = useTranslation();
	const [batchDetails, setBatchDetails] = useState<ReconMediaBatchDetails[]>(
		[],
	);
	const [totalPages, setTotalPages] = useState<number>(0);
	const [pageSize, setPageSize] = useState<number>(DEFAULT_PAGE_SIZE);
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [loading, setLoading] = useState(false);

	const [stats, setStats] = useState({
		avgVariance: 0,
		pendingReconciliation: 0,
		totalReconciledUnits: 0,
	});

	const [sort, setSort] = useState("id");
		  const [order, setOrder] = useState("desc");
		  const mapStatusToApi = (status: string) => {
			if (status === "Expired") return "expired";
			return status;
		  };
		
		  const handleSort = (key: string, value: boolean) => {
			setSort(key);
			setOrder(value ? "asc" : "desc");
			setCurrentPage(1);
		  };

	const [statusFilter, setStatusFilter] = useState("");
	const [permission, setPermission] = useState<any>([]);
	const getPermissionByRole = async (role: number) => {
		try {
		const response = await userService.getPermissionByRole(role);

		if (response.status && response.statusCode === 200) {
			setPermission(response.data.permissions);
		}
		} catch (error) {
		console.error(error);
		}
	};

	useEffect(() => {
		const storedData = localStorage.getItem("userDetails");

		if (storedData) {
		const storedObject = JSON.parse(storedData);
		getPermissionByRole(storedObject[0].role_master_id);
		}
	}, []);

	const getMediaBatchDetails = async () => {
		setLoading(true);
		const queryParams = {
			page: currentPage.toString(),
			size: pageSize.toString(),
			sort,
			order,
			keyword: searchTerm,
			reconciliation_status: statusFilter,
		};

		try {
			const response: ReconMediaBatchListResponse =
				await mediaReconciliationService.getReconMediaBatchList(
					queryParams,
				);
			setBatchDetails(response.data);
			setTotalPages(response.totalPages);
			setCurrentPage(Number(response.page));
			setTotalTableElements(response.totalElements);

			setStats({
				avgVariance: response.avg_variance_per,
				pendingReconciliation: response.pending_reconciliation,
				totalReconciledUnits: response.total_reconciled_units,
			});
		} catch (err) {
			alert("Error getting table data");
		}
		finally {
			setLoading(false);
		}
	};

	const handlePageChange = (page: number, size?: number) => {
		setCurrentPage(page);

		if (size) {
			setPageSize(size);
		}
	};

	useEffect(() => {
		getMediaBatchDetails();
	}, [currentPage, pageSize, searchTerm, statusFilter, sort, order]);
	return (
		<div className="media-prep-scope">
			<MediaPrepHeaderSection />
			<MediaPrepPageHeader pageHeader={t("mediaReconcile.mediaBatch.title")} pageDescription={t('pageDescription.mediaBatchReconciliation.description')} />
			<div className="p-8 max-w-full animate-in fade-in duration-500">
				{/* <ReconTitleHeader
					title={t("mediaReconcile.mediaBatch.title")}
					infoText={t("mediaReconcile.mediaBatch.infoText")}
				/> */}

				<div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-3">
					<ReconStatCard
						label={t(
							"mediaReconcile.mediaBatch.stats.awaitingReconciliation",
						)}
						value={stats.pendingReconciliation}
						sublabel={t(
							"mediaReconcile.mediaBatch.stats.batchesPendingSignOff",
						)}
						accent="orange"
					/>

					<ReconStatCard
						label={t("mediaReconcile.mediaBatch.stats.avgBatchVariance")}
						value={`${stats.avgVariance} %`}
						sublabel={t(
							"mediaReconcile.mediaBatch.stats.withinSopTolerance",
						)}
						accent="green"
					/>

					<ReconStatCard
						label={t(
							"mediaReconcile.mediaBatch.stats.totalReconciledYtd",
						)}
						value={stats.totalReconciledUnits}
						sublabel={t(
							"mediaReconcile.mediaBatch.stats.traceableGMPRecords",
						)}
						variant="dark"
					/>
				</div>
				<div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden flex flex-col">
					<div className="px-6 py-2 bg-slate-50/30 border-b border-gray-200 flex items-center justify-start gap-2">
						<div className="flex items-center gap-1 text-sm text-gray-700">
							<span className="font-semibold">
								{t("mediaReconcile.title")}
							</span>
							<span className="text-gray-400 font-light">|</span>
							<span className="text-gray-500">{totalTableElements}</span>
						</div>
						<ReconSearchFilter
							searchTerm={searchTerm}
							setSearchTerm={setSearchTerm}
							searchPlaceholder={t(
								"mediaReconcile.mediaPowder.searchPlaceHolder",
							)}
							statusFilter={statusFilter}
							setStatusFilter={(value) => {
								setStatusFilter(value);
								setCurrentPage(1);
							}}
						/>
					</div>	
						<MediaPreparationTable
							columns={getMediaBatchReconciliationColumns(t)}
							data={batchDetails}
							currentPage={currentPage}
							loading={loading}
							pageSize={pageSize}
							onSortClick={handleSort}
							emptyMessage={t("mediaReconcile.noDataToDisplay")}
							renderAction={(row: ReconMediaBatchDetails) => (
								permission?.includes("start_media_batch_reconciliation") ? (
								<>
									<button
										onClick={() => setCurrentView(row)}
										className="px-4 py-1.5 bg-[#FF7043] text-white text-[9px] font-black uppercase rounded shadow-sm hover:brightness-110 active:scale-95 transition-all border-none disabled:opacity-50"
									>
										{row.reconciliation_status === "PENDING"
											? t("mediaReconcile.mediaBatch.executeRecon")
											: t("mediaReconcile.mediaBatch.viewDetails")}
									</button>
								</>
								): null
							)}
						/>
					</div>
				</div>
				<Pagination
					page={currentPage}
					totalPages={totalPages}
					handlePageChange={handlePageChange}
				/>
			</div>
		// </div>
	);
};

export default ReconMediaBatchListView;
